import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Wallet, Calculator } from "lucide-react";
import { fmtUsd } from "./FinanceLayout";
import { BANK_CARD, canEditCard } from "@/lib/placement";

const OPERATING = "acct-tea-first-reliance-1403";
const PETTY = "acct-petty-cash-clearing";
const KIND = { check_deposit: "Self-pay check deposit", payroll_float: "Payday float", employer_payment: "Employer invoice payment", landlord_escrow: "Landlord escrow", bill_payment: "Bill payment",
  pmc_payout: "PMC payout", closeout_funding: "Close-out funding", petty_replenish: "Petty Cash replenish", cash_back: "Resident cash-back", imprest_open: "Imprest float opened", opening_deposit: "Opening deposit", manual_deposit: "Deposit" };

const SplitSample = ({ gross }) => {
  const [s, setS] = useState(null);
  useEffect(() => { apiClient.get("/fin/cash/split-preview", { params: { gross } }).then((r) => setS(r.data)).catch(() => {}); }, [gross]);
  if (!s) return null;
  return (
    <div className="border border-nex-border/60 p-3" data-testid={`split-sample-${gross}`}>
      <div className="flex justify-between items-baseline"><span className="serif-head text-xl">{fmtUsd(s.gross)}</span>
        <span className={`mono-label text-[10px] ${s.rule === "safety_floor" ? "text-amber-300" : "text-emerald-400"}`}>{s.rule === "safety_floor" ? "Safety floor · 35 / 35 / 30" : "Standard · $175 / $125"}</span></div>
      <div className="text-xs mt-2 space-y-1">
        <div className="flex justify-between"><span>Rent Escrow</span><span>{fmtUsd(s.rent)}</span></div>
        <div className="flex justify-between"><span>PACT Fee</span><span>{fmtUsd(s.case_management)}</span></div>
        <div className="flex justify-between font-semibold"><span>Resident take-home</span><span>{fmtUsd(s.net)}</span></div>
      </div>
    </div>
  );
};

const AUTO_KINDS = ["check_deposit", "cash_back", "payroll_float", "employer_payment"];

const Register = ({ title, data, testid, onUndo, onRemove }) => (
  <div data-testid={testid}>
    <div className="flex justify-between items-baseline mb-1">
      <span className="mono-label">{title}{data?.account?.opening_balance?.locked && <span className="text-emerald-400 normal-case tracking-normal ml-2" data-testid={`${testid}-opening-locked`}>· opening {fmtUsd(data.account.opening_balance.amount)} locked {data.account.opening_balance.as_of}</span>}</span>
      <span className="serif-head text-xl" data-testid={`${testid}-balance`}>{fmtUsd(data?.account?.balance || 0)}</span>
    </div>
    <div className="max-h-56 overflow-y-auto border border-nex-border/40">
      {(data?.rows || []).length === 0 && <div className="p-3 text-xs text-nex-muted">No postings yet. Automatic postings start Nov 1, 2026.</div>}
      {(data?.rows || []).map((x) => (
        <div key={x.id} className="flex justify-between gap-3 px-3 py-2 border-b border-nex-border/30 text-xs">
          <span><span className="text-nex-muted">{x.date}</span> · {KIND[x.kind] || x.kind.replace(/_/g, " ")}<span className="text-nex-muted block">{x.memo}</span></span>
          <span className="flex items-start gap-2 shrink-0">
            {onRemove && AUTO_KINDS.includes(x.kind) && <button type="button" onClick={() => onRemove(x)} data-testid={`register-remove-correction-${x.id}`}
              className="px-2 py-0.5 text-[10px] font-mono uppercase border border-amber-400/40 text-amber-200 hover:bg-amber-500/10 transition-colors rounded-sm">Remove — Correction Entry</button>}
            {onUndo && x.kind === "petty_replenish" && x.amount > 0 && <button type="button" onClick={() => onUndo(x)} data-testid={`petty-undo-replenish-${x.ref}`}
              className="px-2 py-0.5 text-[10px] font-mono uppercase border border-red-400/40 text-red-300 hover:bg-red-500/10 transition-colors rounded-sm">Undo replenish</button>}
            <span className={`tabular-nums ${x.amount < 0 ? "text-red-300" : "text-emerald-300"}`}>{fmtUsd(x.amount)}<span className="text-nex-muted block text-right">{fmtUsd(x.balance_after)}</span></span>
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const CashLedgerCard = ({ onChange }) => {
  const { user } = useAuth();
  const [op, setOp] = useState(null);
  const [petty, setPetty] = useState(null);
  const [cz, setCz] = useState(null);
  const [held, setHeld] = useState(null);
  const [busy, setBusy] = useState(false);
  const sa = canEditCard(user, BANK_CARD);
  const load = useCallback(() => {
    apiClient.get("/fin/cash/register", { params: { account_id: OPERATING } }).then((r) => setOp(r.data)).catch(() => {});
    apiClient.get("/fin/cash/register", { params: { account_id: PETTY } }).then((r) => setPetty(r.data)).catch(() => {});
    apiClient.get("/fin/cash/resident-savings").then((r) => setHeld(r.data)).catch(() => {});
    apiClient.get("/finance/bank-accounts").then((r) => {
      const c = (r.data.items || []).find((a) => a.retained_for);
      if (c) apiClient.get("/fin/cash/register", { params: { account_id: c.id } }).then((x) => setCz(x.data));
    }).catch(() => {});
  }, []);
  useEffect(() => { if (sa) load(); }, [sa, load]);
  useEffect(() => {  // savings change as paychecks and withdrawals post: keep the liability line current
    if (!sa) return undefined;
    const t = setInterval(() => apiClient.get("/fin/cash/resident-savings").then((r) => setHeld(r.data)).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, [sa]);
  if (!sa || !op || !petty) return null;

  const act = async (path, msg) => {
    setBusy(true);
    try { const r = await apiClient.post(path, {}); toast.success(msg(r.data)); load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const undo = (x) => {
    if (!window.confirm(`Undo the ${fmtUsd(x.amount)} replenishment from ${x.date}? It comes off the drawer, goes back to ...1403, and is logged as undone by you.`)) return;
    act(`/fin/cash/petty/replenish/${x.ref}/undo`, (d) => `Replenishment undone · ${fmtUsd(d.amount)} back to ...1403`);
  };
  const isSa = user?.role === "super_admin" && !user?.preview_by && !user?.login_as_by;
  const remove = (x) => {
    if (!window.confirm(`Remove this ${fmtUsd(x.amount)} entry from ${x.date}? It came from a resident ledger correction, not real cash. It (and its reversal, if any) comes off this register, the balance is rebuilt, and the removal is logged as done by you. The matching entry on the other account (deposit or cash-back) stays until you remove it too.`)) return;
    act(`/fin/cash/register/${x.id}/remove-correction`, (d) => `Correction entry removed · ${d.removed} row${d.removed === 1 ? "" : "s"}`);
  };
  const short = petty?.account ? Math.max((petty.account.imprest || 2000) - petty.account.balance, 0) : 0;

  return (
    <div className="card-industrial p-5 mb-6" data-testid="cash-ledger-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 font-semibold"><Wallet size={16} className="text-nex-gold" /> Cash ledger · TEA-First Reliance ...1403 & Petty Cash</div>
        {petty?.account
          ? <button type="button" disabled={busy || short < 0.005} onClick={() => act("/fin/cash/petty/replenish", (d) => `Petty Cash replenished · ${fmtUsd(d.amount)}`)} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="petty-replenish-btn">
              {short < 0.005 ? "Drawer at $2,000 float" : `Replenish drawer · ${fmtUsd(short)}`}</button>
          : <button type="button" disabled={busy} onClick={() => act("/fin/cash/petty/open", () => "Petty Cash Clearing opened at $2,000.00")} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="petty-open-btn">Open Petty Cash ($2,000 float)</button>}
      </div>
      <div className="mb-4 text-sm" data-testid="cash-total">Starting cash across active accounts: <span className="font-semibold">{fmtUsd((op.account?.balance || 0) + (petty.account?.balance || 0) + (cz?.account?.balance || 0))}</span></div>
      {held && (
        <div className="mb-4 border border-amber-400/40 bg-amber-400/[0.06] px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="resident-savings-held">
          <div><span className="font-semibold">Resident Savings Held</span> <span className="mono-label text-[10px] text-amber-300 ml-1">LIABILITY</span>
            <div className="text-xs text-nex-muted">Held in TEA-First Reliance ...1403 — set at {fmtUsd(held.baseline)} on {String(held.baseline_as_of || "").slice(0, 10)}{held.change_since ? `, ${held.change_since > 0 ? "+" : "−"}${fmtUsd(Math.abs(held.change_since))} savings activity since` : ""}. Belongs to the residents, not TEA.</div></div>
          <div className="text-right"><span className="serif-head text-xl text-amber-200" data-testid="resident-savings-held-total">{fmtUsd(held.total)}</span>
            <div className="text-xs text-nex-muted" data-testid="resident-savings-held-net">Usable ...1403 balance: <span className="text-white font-semibold">{fmtUsd((op.account?.balance || 0) - held.total)}</span></div></div>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-5">
        <Register onRemove={isSa ? remove : null} title="TEA-First Reliance ...1403 · operating" data={op} testid="register-operating" />
        {cz && <Register title={`${cz.account.nickname} ...${cz.account.last4} · ${cz.account.legal_name || "legacy"}`} data={cz} testid="register-citizens" />}
        <Register title="Petty Cash Clearing · drawer" data={petty} testid="register-petty" onUndo={isSa ? undo : null} onRemove={isSa ? remove : null} />
      </div>
      <div className="mt-5">
        <div className="mono-label mb-2 flex items-center gap-2"><Calculator size={13} /> Paycheck split · Prorated Safety Floor (under $428.57 → 35% Rent · 35% PACT Fee · 30% resident)</div>
        <div className="grid grid-cols-2 gap-3"><SplitSample gross={350} /><SplitSample gross={600} /></div>
      </div>
    </div>
  );
};
